import type { AnalyticsEvent } from '../types'

/** Source of truth for the `event` strings GTM triggers match against. See README. */
export const GTM_EVENT_NAME: Record<AnalyticsEvent['type'], string> = {
  CUSTOM_PAGE_VIEW: 'custom_page_view',
  DYNAMIC_CLICK_TRACKING: 'dynamic_click_tracking',
  LOGIN: 'login',
  LOGIN_ATTEMPT: 'login_attempt',
  LOGOUT: 'logout',
  SEARCH: 'search',
  // SDR uses camelCase here, unlike snake_case `search`.
  SEARCH_REFINEMENT: 'searchRefinement',
  VIEW_ITEM_LIST: 'view_item_list',
  SELECT_ITEM: 'select_item',
}

/** Login/click attribution keys. Owned by click+login events; cross-cleared (`undefined`) by all other event pushes to prevent leakage into merged-state. */
export const PER_EVENT_KEY_NAMES = ['event_category', 'event_action', 'event_label'] as const

/** Every `SearchEvent.payload` key — emitted on every SEARCH push (self-contained); cross-cleared by other events. */
export const SEARCH_PER_EVENT_KEY_NAMES = [
  'searchType',
  'searchKeyword',
  'searchKeywordTyped',
  'searchResultsCount',
  'searchCheckInDate',
  'searchCheckOutDate',
  'searchMonthSelection',
  'searchNumOfNights',
  'searchSelectionOption',
  'recentSearch',
  'bonusTimeSearch',
  'datesFlexible',
  'flexible_dates_search_params',
  'searchErrorType',
  'searchErrorMessage',
] as const

/** Every `SearchRefinementEvent.payload` key (incl. `searchKeyword`, shared with SEARCH). */
export const SEARCH_REFINEMENT_PER_EVENT_KEY_NAMES = ['searchRefineType', 'searchRefineValue', 'searchKeyword'] as const

/** Every `ViewItemListEvent.payload` key. Cross-cleared by other events so `ecommerce` / attribution can't leak forward. */
export const VIEW_ITEM_LIST_PER_EVENT_KEY_NAMES = ['searchKeyword', 'searchSortValue', 'ecommerce'] as const

/** Every `SelectItemEvent.payload` key. Shares `searchKeyword` with SEARCH; `ecommerce` with VIEW_ITEM_LIST. */
export const SELECT_ITEM_PER_EVENT_KEY_NAMES = ['searchKeyword', 'ecommerce'] as const

/** Deduplicated union of every per-event key (the `Set` collapses shared `searchKeyword` + `ecommerce`). */
export const ALL_PER_EVENT_KEY_NAMES = Array.from(
  new Set<string>([
    ...PER_EVENT_KEY_NAMES,
    ...SEARCH_PER_EVENT_KEY_NAMES,
    ...SEARCH_REFINEMENT_PER_EVENT_KEY_NAMES,
    ...VIEW_ITEM_LIST_PER_EVENT_KEY_NAMES,
    ...SELECT_ITEM_PER_EVENT_KEY_NAMES,
  ]),
)

/** Per-event keys this event type sets values for (the rest get cleared in its push). */
const OWNED_PER_EVENT_KEYS: Record<AnalyticsEvent['type'], readonly string[]> = {
  CUSTOM_PAGE_VIEW: [],
  DYNAMIC_CLICK_TRACKING: [...PER_EVENT_KEY_NAMES],
  LOGIN: [...PER_EVENT_KEY_NAMES],
  LOGIN_ATTEMPT: [...PER_EVENT_KEY_NAMES],
  LOGOUT: [...PER_EVENT_KEY_NAMES],
  SEARCH: [...SEARCH_PER_EVENT_KEY_NAMES],
  SEARCH_REFINEMENT: [...SEARCH_REFINEMENT_PER_EVENT_KEY_NAMES],
  VIEW_ITEM_LIST: [...VIEW_ITEM_LIST_PER_EVENT_KEY_NAMES],
  SELECT_ITEM: [...SELECT_ITEM_PER_EVENT_KEY_NAMES],
}

/**
 * Per-event keys this event type does NOT own — emitted as `undefined` in its push so a prior
 * event's values can't leak into merged-state. Folded into every event push to eliminate the
 * trailing-clearer pattern (which serialized as visible `{}` noise in dataLayer dumps).
 */
function crossClearKeysForEvent(eventType: AnalyticsEvent['type']): Record<string, undefined> {
  const owned = new Set<string>(OWNED_PER_EVENT_KEYS[eventType])
  return Object.fromEntries(ALL_PER_EVENT_KEY_NAMES.filter((k) => !owned.has(k)).map((k) => [k, undefined]))
}

/** One-time keys-only push that registers every per-event key in GTM's variables view. */
export function primeDataLayerKeyOrder(): void {
  if (typeof window === 'undefined') return
  window.dataLayer?.push(Object.fromEntries(ALL_PER_EVENT_KEY_NAMES.map((key) => [key, undefined])))
}

/**
 * Pushes the event as a single self-cleaning entry: own keys with values, then merged globals,
 * then every other per-event key as `undefined` so a prior event can't leak into merged-state.
 * No trailing clearer push (that pattern serialized as visible `{}` noise in dataLayer dumps).
 *
 * Exception: VIEW_ITEM_LIST and SELECT_ITEM are GA4 ecommerce events. GA4's gtag library keeps an
 * internal `items` cache that ACCUMULATES across pushes when both pushes carry an `ecommerce`
 * object, so they each emit a separate leading `{ ecommerce: null }` push to reset that cache —
 * the documented pattern, not a per-event self-clean.
 */
export function dispatchGtmEvent<E extends AnalyticsEvent>(event: E): void {
  const crossClear = crossClearKeysForEvent(event.type)
  switch (event.type) {
    case 'CUSTOM_PAGE_VIEW': {
      const { custom_page_name, ...rest } = event.payload
      window.dataLayer?.push({
        event: GTM_EVENT_NAME.CUSTOM_PAGE_VIEW,
        custom_page_name,
        ...rest,
        ...crossClear,
      })
      break
    }
    case 'DYNAMIC_CLICK_TRACKING': {
      const { event_label, ...rest } = event.payload
      window.dataLayer?.push({
        event: GTM_EVENT_NAME.DYNAMIC_CLICK_TRACKING,
        event_category: 'dynamic click tracking',
        event_action: 'click',
        event_label,
        ...rest,
        ...crossClear,
      })
      break
    }
    case 'LOGIN': {
      const { custom_page_name, ...rest } = event.payload
      window.dataLayer?.push({
        event: GTM_EVENT_NAME.LOGIN,
        event_category: 'login',
        event_action: 'click',
        event_label: 'success',
        custom_page_name,
        ...rest,
        ...crossClear,
      })
      break
    }
    case 'LOGIN_ATTEMPT':
      window.dataLayer?.push({
        event: GTM_EVENT_NAME.LOGIN_ATTEMPT,
        event_category: 'login',
        event_action: 'click',
        event_label: 'login_attempt',
        ...event.payload,
        ...crossClear,
      })
      break
    case 'LOGOUT': {
      const { custom_page_name, ...rest } = event.payload
      window.dataLayer?.push({
        event: GTM_EVENT_NAME.LOGOUT,
        event_category: 'logout',
        event_action: 'click',
        event_label: 'success',
        custom_page_name,
        ...rest,
        ...crossClear,
      })
      break
    }
    case 'SEARCH': {
      // Self-contained: every SearchEvent.payload key on every push (with `undefined` for omitted
      // optional fields) so consecutive SEARCHes can't leak via merged-state. Required keys lead.
      const {
        searchType,
        searchKeyword,
        searchKeywordTyped,
        searchResultsCount,
        searchCheckInDate,
        searchCheckOutDate,
        searchMonthSelection,
        searchNumOfNights,
        searchSelectionOption,
        recentSearch,
        bonusTimeSearch,
        datesFlexible,
        flexible_dates_search_params,
        searchErrorType,
        searchErrorMessage,
        ...globals
      } = event.payload
      window.dataLayer?.push({
        event: GTM_EVENT_NAME.SEARCH,
        searchType,
        recentSearch,
        bonusTimeSearch,
        datesFlexible,
        searchKeyword,
        searchKeywordTyped,
        searchResultsCount,
        searchCheckInDate,
        searchCheckOutDate,
        searchMonthSelection,
        searchNumOfNights,
        searchSelectionOption,
        flexible_dates_search_params,
        searchErrorType,
        searchErrorMessage,
        ...globals,
        ...crossClear,
      })
      break
    }
    case 'SEARCH_REFINEMENT': {
      // One refinement per push; self-contained like SEARCH. Callers emit one event per added filter.
      const { searchRefineType, searchRefineValue, searchKeyword, ...globals } = event.payload
      window.dataLayer?.push({
        event: GTM_EVENT_NAME.SEARCH_REFINEMENT,
        searchRefineType,
        searchRefineValue,
        searchKeyword,
        ...globals,
        ...crossClear,
      })
      break
    }
    case 'VIEW_ITEM_LIST': {
      // GA4-canonical: `items` nested under `ecommerce`. Per-item `item_list_name` carries the list label.
      // `searchKeyword` mirrors the parent SEARCH attribution.
      // `{ ecommerce: null }` first: GA4's gtag accumulates `items` across consecutive ecommerce pushes
      // unless you explicitly null the object between events (documented at
      // developers.google.com/tag-platform/gtagjs/datalayer#reset_data_layer_variables). Without it, a
      // `view_item_list (resorts) → select_item` (or `view_item_list (resorts) → view_item_list (suites)`)
      // sequence ships the prior list's items concatenated onto the new event.
      window.dataLayer?.push({ ecommerce: null })
      const { searchKeyword, searchSortValue, ecommerce, ...globals } = event.payload
      window.dataLayer?.push({
        event: GTM_EVENT_NAME.VIEW_ITEM_LIST,
        searchKeyword,
        searchSortValue,
        ecommerce,
        ...globals,
        ...crossClear,
      })
      break
    }
    case 'SELECT_ITEM': {
      // GA4-canonical: `items` nested under `ecommerce`; `searchKeyword` mirrors the parent SEARCH attribution.
      // `{ ecommerce: null }` first: same gtag-accumulation guard as VIEW_ITEM_LIST. Without this, a click
      // from a populated resort list ships every list row under `ecommerce.items` instead of just the clicked one.
      window.dataLayer?.push({ ecommerce: null })
      const { searchKeyword, ecommerce, ...globals } = event.payload
      window.dataLayer?.push({
        event: GTM_EVENT_NAME.SELECT_ITEM,
        searchKeyword,
        ecommerce,
        ...globals,
        ...crossClear,
      })
      break
    }
  }
}
